import React, { useEffect, useState } from 'react';
import { Alert, Col, Form, Row, Spinner } from 'react-bootstrap';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+-]+$/;

const CheckoutClientForm = ({ data, updateFormData, submitting, onValidityChange }) => {
  const [ruc, setRuc] = useState(data.ruc || '');
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [error, setError] = useState('');
  const [clientFound, setClientFound] = useState(!!data.name);

  const handleRucLookup = async () => {
    if (!ruc || loadingLookup || submitting) {
      return;
    }

    if (clientFound && ruc === data.ruc) {
      return;
    }

    setLoadingLookup(true);
    setError('');
    setClientFound(false);

    let foundClientData = null;

    for (const type of [2, 1]) {
      try {
        const response = await fetch(`https://hub.efacturapty.com/Estimate/Taxpayer/${type}/${ruc}`);
        if (response.ok) {
          const result = await response.json();
          if (result && result.name) {
            foundClientData = {
              ...result,
              type: type === 1 ? 'Natural' : 'Juridico',
            };
            break;
          }
        }
      } catch (lookupError) {
        console.error(`Error buscando RUC para tipo ${type}:`, lookupError);
      }
    }

    setLoadingLookup(false);

    if (foundClientData) {
      updateFormData({
        ...data,
        ...foundClientData,
        ruc: foundClientData.ruc || ruc,
      });
      setClientFound(true);
      return;
    }

    setError('El RUC no fue encontrado o es invalido. Por favor, verifica el numero.');
    updateFormData({
      ...data,
      dv: '',
      type: '',
      name: '',
    });
  };

  const handleRucChange = (event) => {
    const nextRuc = event.target.value;
    setRuc(nextRuc);

    if (clientFound) {
      setClientFound(false);
      setError('');
    }

    updateFormData({
      ...data,
      ruc: nextRuc,
      dv: '',
      type: '',
      name: '',
    });
  };

  const handleFieldChange = (event) => {
    updateFormData({
      ...data,
      [event.target.name]: event.target.value,
    });
  };

  const emailValue = String(data.email || '').trim();
  const phoneValue = String(data.phone || '').trim();
  const isEmailValid = EMAIL_REGEX.test(emailValue);
  const isPhoneValid = !phoneValue || PHONE_REGEX.test(phoneValue);
  const isFormReady = !loadingLookup && clientFound && isEmailValid && isPhoneValid;

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isFormReady);
    }
  }, [isFormReady, onValidityChange]);

  return (
    <div className="mt-4">
      <h5>Datos del cliente</h5>
      <Form>
        <Row className="mb-3 g-3">
          <Form.Group as={Col} sm={8} controlId="checkout-ruc">
            <Form.Label>
              RUC
              {loadingLookup && <Spinner as="span" animation="border" size="sm" className="ms-2" />}
            </Form.Label>
            <Form.Control
              type="text"
              value={ruc}
              onChange={handleRucChange}
              onBlur={handleRucLookup}
              disabled={loadingLookup || submitting}
            />
          </Form.Group>
          <Form.Group as={Col} sm={4} controlId="checkout-dv">
            <Form.Label>DV</Form.Label>
            <Form.Control type="text" readOnly disabled={!clientFound} value={data.dv || ''} />
          </Form.Group>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3 g-3">
          <Form.Group as={Col} sm={8} controlId="checkout-name">
            <Form.Label>Nombre / Razón Social</Form.Label>
            <Form.Control type="text" readOnly disabled={!clientFound} value={data.name || ''} />
          </Form.Group>

          <Form.Group as={Col} sm={4} controlId="checkout-type">
            <Form.Label>Tipo</Form.Label>
            <Form.Control type="text" readOnly disabled={!clientFound} value={data.type || ''} />
          </Form.Group>
        </Row>

        <Row className="mb-3 g-3">
          <Form.Group as={Col} sm={8} controlId="checkout-email">
            <Form.Label>Correo electrónico</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={data.email || ''}
              onChange={handleFieldChange}
              disabled={submitting}
              isInvalid={emailValue.length > 0 && !isEmailValid}
            />
            <Form.Control.Feedback type="invalid">
              Ingresa un correo electrónico válido.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group as={Col} sm={4} controlId="checkout-phone">
            <Form.Label>Teléfono</Form.Label>
            <Form.Control
              type="text"
              name="phone"
              value={data.phone || ''}
              onChange={handleFieldChange}
              disabled={submitting}
              isInvalid={phoneValue.length > 0 && !isPhoneValid}
            />
            <Form.Control.Feedback type="invalid">
              El teléfono solo puede contener números, guiones y el símbolo +.
            </Form.Control.Feedback>
          </Form.Group>
        </Row>

        {clientFound && data.type === 'Juridico' && (
          <Form.Group className="mb-3" controlId="checkout-contactPerson">
            <Form.Label>Nombre de contacto</Form.Label>
            <Form.Control
              type="text"
              name="contactPerson"
              value={data.contactPerson || ''}
              onChange={handleFieldChange}
              disabled={submitting}
            />
          </Form.Group>
        )}

      </Form>
    </div>
  );
};

export default CheckoutClientForm;
